// What changed since the window before.
//
// Shared by Keyword Performance and Search Attribution, each showing the half
// it owns — terms that slipped or were taken on one, products leaning harder
// on a single term or losing search share on the other. One component because
// the shape is the same; two surfaces because the questions are not.
//
// Everything here is a comparison against the previous window, so a standing
// fact is never repeated as news. Where no earlier window was collected there
// is nothing to compare, and saying that is different from saying nothing
// changed.
import React from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

export default function WhatChanged({ data, styles, onOpen, emptyLine }) {
  const s = styles;
  if (!data) return <div className={s.empty}>Loading…</div>;

  // Nothing to compare against and nothing having changed are different
  // answers, and only one of them is about the seller's business. Saying
  // "nothing changed" when no earlier window was collected asserts a check
  // that never ran.
  if (!data.compared_to) {
    return (
      <div className={s.empty}>
        <p>Nothing to compare against yet.</p>
        <p className={s.hint}>
          This reads the window before the one you are looking at, and that window has not
          been collected. Collect a second {(data.period || 'period').toLowerCase()} — or
          switch to a period that already has two — and the comparison appears.
        </p>
      </div>
    );
  }

  if (!data.alerts?.length) {
    return (
      <div className={s.empty}>
        <p>{emptyLine || 'Nothing changed enough this window to raise.'}</p>
        <p className={s.hint}>Compared with {data.compared_to}.</p>
        {data.competitor_comparison && data.competitor_comparison !== 'ok' && (
          <p className={s.hint}>{data.competitor_comparison}.</p>
        )}
      </div>
    );
  }

  return (
    <>
      <p className={s.hint} style={{ marginBottom: 12 }}>
        Compared with {data.compared_to}.
        {data.competitor_comparison && data.competitor_comparison !== 'ok' && (
          <> Competitor changes are not included: {data.competitor_comparison}.</>
        )}
      </p>
      <div className={s.changeList}>
        {data.alerts.map((a, i) => (
          <div key={`${a.kind}-${a.subject}-${i}`}
               className={`${s.change} ${a.severity === 'high' ? s.changeHigh : s.changeMedium}`}>
            <span className={s.changeIcon}>
              {a.severity === 'high' ? <FiAlertTriangle /> : <FiInfo />}
            </span>
            <div>
              <div className={s.changeHead}>
                {onOpen ? (
                  <button className={s.link} onClick={() => onOpen(a.subject)}>{a.subject}</button>
                ) : <strong>{a.subject}</strong>}
              </div>
              <div className={s.changeLine}>{a.headline}</div>
              {a.detail && <div className={s.hint}>{a.detail}</div>}
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
